import { baseApi } from "./baseApi";

/**
 * Blog authoring API (`/api/blog` on n450s_backend) — admin + `writer` role.
 *
 * Reading is separate: published articles come from `agentApi`'s
 * `getArticles` / `getArticle` (`/api/agent/blog`), which serves both the
 * sitebot's posts and human ones. Everything here can see drafts, so it is only
 * ever mounted behind `RequireWriterRoute`.
 */

export type ArticleStatus = "draft" | "published";

export type ArticleWaybackStatus =
  | "idle"
  | "queued"
  | "requested"
  | "captured"
  | "failed";

export interface ArticleWayback {
  status: ArticleWaybackStatus;
  snapshotUrl?: string;
  capturedAt?: string;
  requestedAt?: string;
  scheduledAt?: string;
  timestamp?: string;
  attempts: number;
  error?: string;
  requestedBy?: string;
  /** the backend-rendered permalink that actually gets archived */
  targetUrl: string;
  /** a capture is running right now */
  pending: boolean;
  /** set instead of `pending` when a failed attempt is waiting out its backoff */
  retryAt?: string;
}

export interface ArticleAuthor {
  type: "agent" | "user";
  name: string;
  label: string;
}

export interface BlogArticle {
  _id: string;
  slug: string;
  /** set when this article is a chapter of a book */
  bookId?: string;
  chapterNumber?: number;
  title: string;
  subtitle?: string;
  excerpt?: string;
  content?: string;
  coverImageUrl?: string;
  category: string;
  tags: string[];
  status: ArticleStatus;
  authorType: "agent" | "user";
  authorUsername?: string;
  agentName?: string;
  author: ArticleAuthor;
  publishedAt: string;
  createdAt?: string;
  updatedAt?: string;
  wayback?: ArticleWayback;
}

export interface BlogMeta {
  categories: string[];
  username?: string;
  isAdmin: boolean;
}

export interface ArticleDraftInput {
  title: string;
  subtitle?: string;
  content: string;
  category?: string;
  tags?: string[];
  coverImageUrl?: string;
  status?: ArticleStatus;
  /** id of the book this belongs to, or null to detach it back to a standalone post */
  bookId?: string | null;
  chapterNumber?: number;
}

export interface BlogBook {
  _id: string;
  slug: string;
  title: string;
  subtitle?: string;
  excerpt?: string;
  description?: string;
  coverImageUrl?: string;
  authorUsername?: string;
  tags: string[];
  status: ArticleStatus;
  complete: boolean;
  publishedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  chapterCount: number;
  chapters?: BlogArticle[];
  wayback?: ArticleWayback;
}

export interface BookDraftInput {
  title: string;
  subtitle?: string;
  description?: string;
  coverImageUrl?: string;
  tags?: string[];
  complete?: boolean;
  status?: ArticleStatus;
}

export const blogApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBlogMeta: builder.query<BlogMeta, void>({
      query: () => "/blog/meta",
    }),

    /** Authoring list — drafts included. Admins see everything unless scope is "mine". */
    getMyArticles: builder.query<
      { articles: BlogArticle[] },
      { scope?: "mine" | "all"; status?: ArticleStatus } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && args.scope) params.set("scope", args.scope);
        if (args && args.status) params.set("status", args.status);
        const qs = params.toString();
        return `/blog/articles${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["BlogDrafts"],
    }),

    getArticleForEdit: builder.query<BlogArticle, string>({
      query: (id) => `/blog/articles/${id}`,
      providesTags: (_r, _e, id) => [{ type: "BlogDrafts" as const, id }],
    }),

    createArticle: builder.mutation<BlogArticle, ArticleDraftInput>({
      query: (body) => ({ url: "/blog/articles", method: "POST", body }),
      invalidatesTags: ["BlogDrafts", "BlogArticles"],
    }),

    updateArticle: builder.mutation<
      BlogArticle,
      { id: string; patch: Partial<ArticleDraftInput> }
    >({
      query: ({ id, patch }) => ({
        url: `/blog/articles/${id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "BlogDrafts",
        "BlogArticles",
        { type: "BlogDrafts" as const, id },
      ],
    }),

    publishArticle: builder.mutation<BlogArticle, string>({
      query: (id) => ({ url: `/blog/articles/${id}/publish`, method: "POST" }),
      invalidatesTags: ["BlogDrafts", "BlogArticles"],
    }),

    unpublishArticle: builder.mutation<BlogArticle, string>({
      query: (id) => ({ url: `/blog/articles/${id}/unpublish`, method: "POST" }),
      invalidatesTags: ["BlogDrafts", "BlogArticles"],
    }),

    deleteArticle: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/blog/articles/${id}`, method: "DELETE" }),
      invalidatesTags: ["BlogDrafts", "BlogArticles"],
    }),

    /** Kicks off the Wayback capture; returns 202 with the queued state. */
    archiveArticle: builder.mutation<
      { alreadyRunning: boolean; wayback: ArticleWayback },
      string
    >({
      query: (id) => ({ url: `/blog/articles/${id}/wayback`, method: "POST" }),
      invalidatesTags: (_r, _e, id) => [{ type: "BlogWayback" as const, id }],
    }),

    /** Polled by the archive button while a capture is in flight. */
    getArticleWayback: builder.query<{ wayback: ArticleWayback }, string>({
      query: (id) => `/blog/articles/${id}/wayback`,
      providesTags: (_r, _e, id) => [{ type: "BlogWayback" as const, id }],
    }),

    // ── books ───────────────────────────────────────────────────────────────

    getMyBooks: builder.query<{ books: BlogBook[] }, { scope?: "mine" | "all" } | void>({
      query: (args) =>
        `/blog/books${args && args.scope === "mine" ? "?scope=mine" : ""}`,
      providesTags: ["BlogBooks"],
    }),

    getBookForEdit: builder.query<BlogBook, string>({
      query: (id) => `/blog/books/${id}`,
      providesTags: (_r, _e, id) => [{ type: "BlogBooks" as const, id }],
    }),

    createBook: builder.mutation<BlogBook, BookDraftInput>({
      query: (body) => ({ url: "/blog/books", method: "POST", body }),
      invalidatesTags: ["BlogBooks"],
    }),

    updateBook: builder.mutation<BlogBook, { id: string; patch: Partial<BookDraftInput> }>({
      query: ({ id, patch }) => ({
        url: `/blog/books/${id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_r, _e, { id }) => ["BlogBooks", { type: "BlogBooks" as const, id }],
    }),

    publishBook: builder.mutation<BlogBook, string>({
      query: (id) => ({ url: `/blog/books/${id}/publish`, method: "POST" }),
      invalidatesTags: ["BlogBooks"],
    }),

    unpublishBook: builder.mutation<BlogBook, string>({
      query: (id) => ({ url: `/blog/books/${id}/unpublish`, method: "POST" }),
      invalidatesTags: ["BlogBooks"],
    }),

    /** Renumbers chapters 1..n in the given order. */
    reorderChapters: builder.mutation<BlogBook, { id: string; order: string[] }>({
      query: ({ id, order }) => ({
        url: `/blog/books/${id}/chapters/reorder`,
        method: "POST",
        body: { order },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "BlogBooks",
        "BlogDrafts",
        { type: "BlogBooks" as const, id },
      ],
    }),

    /** Deletes the book only — chapters survive as standalone posts. */
    deleteBook: builder.mutation<{ ok: boolean; detachedChapters: number }, string>({
      query: (id) => ({ url: `/blog/books/${id}`, method: "DELETE" }),
      invalidatesTags: ["BlogBooks", "BlogDrafts"],
    }),

    archiveBook: builder.mutation<
      { alreadyRunning: boolean; wayback: ArticleWayback },
      string
    >({
      query: (id) => ({ url: `/blog/books/${id}/wayback`, method: "POST" }),
      invalidatesTags: (_r, _e, id) => [{ type: "BlogWayback" as const, id }],
    }),

    getBookWayback: builder.query<{ wayback: ArticleWayback }, string>({
      query: (id) => `/blog/books/${id}/wayback`,
      providesTags: (_r, _e, id) => [{ type: "BlogWayback" as const, id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBlogMetaQuery,
  useGetMyArticlesQuery,
  useGetArticleForEditQuery,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  usePublishArticleMutation,
  useUnpublishArticleMutation,
  useDeleteArticleMutation,
  useArchiveArticleMutation,
  useGetArticleWaybackQuery,
  useGetMyBooksQuery,
  useGetBookForEditQuery,
  useCreateBookMutation,
  useUpdateBookMutation,
  usePublishBookMutation,
  useUnpublishBookMutation,
  useReorderChaptersMutation,
  useDeleteBookMutation,
  useArchiveBookMutation,
  useGetBookWaybackQuery,
} = blogApi;
